import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import moment from 'moment';
import {
  Box,
  Input,
  Textarea,
  FormControl,
  FormLabel,
  Select,
  Grid,
  GridItem,
  Spinner,
  Alert,
  AlertIcon,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
} from "@chakra-ui/react";
import { useDropzone } from "react-dropzone";
import * as IconButton from "../themes/IconButtonSet";
import TextSet from "../themes/TextSet";
import { ViewIcon, DeleteIcon } from "@chakra-ui/icons";
import { FaFilePdf } from "react-icons/fa";

const API_URL = process.env.REACT_APP_API_URL;
const BASE_URL = process.env.REACT_APP_BASE_URL || "http://localhost:3003";
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

const AssetForm = () => {
  const { AssetID } = useParams();
  const decodedAssetID = decodeURIComponent(AssetID);
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    AssetID: "",
    AssetCode: "",
    BudgetYear: "",
    AcquisitionDate: "",
    AssetName: "",
    AssetCategoryID: "",
    AssetTypeID: "",
    AcquisitionType: "",
    DepartmentID: "",
    CostUnitID: "",
    UnitPrice: "",
    Quantity: "1",
    WarrantyBy: "",
    WarrantyTel: "",
    WarrantyEmail: "",
    WarrantyExpired: "",
    MaintenanceBy: "",
    MaintenanceTel: "",
    MaintenanceEmail: "",
    MaintenanceExpired: "",
    Remark: "",
    Status: "1",
    CreateBy: "",
    UpdatedBy: "",
    CreateDate: "",
    UpdatedDate: "",
    AttachFilePath01: "",
    AttachFilePath02: "",
    AttachFilePath03: "",
    AttachFilePath04: "",
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [assetTypes, setAssetTypes] = useState([]);
  const [assetCategories, setAssetCategories] = useState([]);
  const [acquisitionTypes, setAcquisitionTypes] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [costCenters, setCostCenters] = useState([]);
  const [attachments, setAttachments] = useState([]);

  useEffect(() => {
    fetchInitialData();
    if (AssetID) {
      fetchAssetData();
    }
  }, [AssetID]);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const typesResponse = await fetch(`${API_URL}/getAssetTypesList`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      const typesResult = await typesResponse.json();
      if (typesResult.result === "success" && typesResult.List) {
        setAssetTypes(typesResult.List);
      }

      const categoriesResponse = await fetch(`${API_URL}/getAssetCategoriesList`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      const categoriesResult = await categoriesResponse.json();
      if (categoriesResult.result === "success" && categoriesResult.List) {
        setAssetCategories(categoriesResult.List);
      }

      const acquisitionResponse = await fetch(`${API_URL}/getAcquisitionTypesList`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      const acquisitionResult = await acquisitionResponse.json();
      if (acquisitionResult.result === "success" && acquisitionResult.List) {
        setAcquisitionTypes(acquisitionResult.List);
      }

      const departmentsResponse = await fetch(`${API_URL}/getOrganizationDepartmentsList`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      const departmentsResult = await departmentsResponse.json();
      if (departmentsResult.result === "success" && departmentsResult.List) {
        setDepartments(departmentsResult.List);
      }

      const costCentersResponse = await fetch(`${API_URL}/getCostCenterList`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      const costCentersResult = await costCentersResponse.json();
      if (costCentersResult.result === "success" && costCentersResult.List) {
        setCostCenters(costCentersResult.List);
      }
    } catch (err) {
      setError("เกิดข้อผิดพลาดในการดึงข้อมูลเริ่มต้น");
    } finally {
      setLoading(false);
    }
  };

  const formatDateForInput = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    if (isNaN(date)) return "";
    return date.toISOString().slice(0, 10);
  };

  const fetchAssetData = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/getAssetByID`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ AssetID: decodedAssetID }),
      });

      const result = await response.json();
      if (result.result === "success" && result.Asset) {
        const assetData = result.Asset;
        setFormData({
          ...assetData,
          Status: assetData.Status ? "1" : "0",
          AcquisitionDate: formatDateForInput(assetData.AcquisitionDate),
          WarrantyExpired: formatDateForInput(assetData.WarrantyExpired),
          MaintenanceExpired: formatDateForInput(assetData.MaintenanceExpired),
          CreateDate: formatDateForInput(assetData.CreateDate),
          UpdatedDate: formatDateForInput(assetData.UpdatedDate),
          BudgetYear: assetData.BudgetYear || "",
          UnitPrice: assetData.UnitPrice || "",
          Quantity: assetData.Quantity || "1",
        });

        const existingAttachments = [
          { field: "AttachFilePath01", path: assetData.AttachFilePath01 },
          { field: "AttachFilePath02", path: assetData.AttachFilePath02 },
          { field: "AttachFilePath03", path: assetData.AttachFilePath03 },
          { field: "AttachFilePath04", path: assetData.AttachFilePath04 },
        ].filter(att => att.path).map(att => ({
          FileName: att.path.split("/").pop(),
          preview: `${BASE_URL}${att.path}`,
          isExisting: true,
          field: att.field,
        }));

        setAttachments(existingAttachments);
      } else {
        setError(result.error || "ไม่พบข้อมูลครุภัณฑ์");
      }
    } catch (err) {
      setError("เกิดข้อผิดพลาดในการดึงข้อมูล");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: (acceptedFiles) => {
      if (attachments.length + acceptedFiles.length > 4) {
        setError("สามารถแนบไฟล์ได้สูงสุด 4 ไฟล์เท่านั้น");
        return;
      }

      const validFiles = [];
      const oversizedFiles = [];

      acceptedFiles.forEach(file => {
        if (file.size > MAX_FILE_SIZE) {
          oversizedFiles.push(file);
        } else {
          validFiles.push(file);
        }
      });

      if (oversizedFiles.length > 0) {
        setError(`ไฟล์ต่อไปนี้มีขนาดเกิน 10MB: ${oversizedFiles.map(file => file.name).join(", ")}`);
        return;
      }

      const newAttachments = validFiles.map(file => ({
        file,
        FileName: file.name,
        FileSize: file.size,
        preview: URL.createObjectURL(file),
        isExisting: false,
      }));

      setAttachments([...attachments, ...newAttachments]);
    },
    multiple: true,
    accept: {
      "image/*": [".jpeg", ".jpg", ".png", ".gif"],
      "application/pdf": [".pdf"],
      "application/msword": [".doc"],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
    },
  });

  const handleViewFile = (attachment) => {
    if (attachment.preview) {
      window.open(attachment.preview, "_blank");
    } else {
      setError("ไม่สามารถดูไฟล์ได้");
    }
  };

  const handleDeleteAttachment = async (index) => {
    const confirmDelete = window.confirm("คุณแน่ใจหรือไม่ว่าต้องการลบไฟล์นี้?");
    if (!confirmDelete) return;

    const att = attachments[index];
    if (att.isExisting && att.field) {
      try {
        await fetch(`${API_URL}/deleteAssetAttachment`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ AssetID, FileField: att.field }),
        });
      } catch (err) {
        setError("เกิดข้อผิดพลาดในการลบไฟล์แนบ");
        return;
      }
    }
    const updatedAttachments = attachments.filter((_, i) => i !== index);
    setAttachments(updatedAttachments);
  };

  const handleSubmit = async () => {
    if (!formData.AssetCode.trim() || !formData.AssetName.trim()) {
      setError("กรุณากรอกข้อมูลให้ครบถ้วน: รหัสครุภัณฑ์ และ ชื่อครุภัณฑ์");
      return;
    }

    setLoading(true);
    setError("");
    try {
      const url = AssetID ? `${API_URL}/updateAssetByID` : `${API_URL}/createAsset`;
      const formDataWithFiles = new FormData();

      const finalFormData = {
        ...formData,
        AssetID: formData.AssetCode, // Set AssetID to AssetCode
        AssetCode: formData.AssetCode, // Ensure AssetCode is included
        CreateBy: formData.CreateBy || "admin",
        UpdatedBy: formData.UpdatedBy || "admin",
        UnitPrice: formData.UnitPrice && !isNaN(parseFloat(formData.UnitPrice)) ? parseFloat(formData.UnitPrice) : 0,
        WarrantyExpired: formData.WarrantyExpired && moment(formData.WarrantyExpired, moment.ISO_8601, true).isValid() ? formData.WarrantyExpired : '',
        MaintenanceExpired: formData.MaintenanceExpired && moment(formData.MaintenanceExpired, moment.ISO_8601, true).isValid() ? formData.MaintenanceExpired : '',
      };

      console.log('Form Data:', finalFormData);

      for (const key in finalFormData) {
        if (finalFormData[key] !== undefined && finalFormData[key] !== null && finalFormData[key] !== '') {
          formDataWithFiles.append(key, finalFormData[key]);
        }
      }

      console.log('Attachments:', attachments);

      attachments.forEach((att, index) => {
        if (att && att.file && att.file instanceof File) {
          formDataWithFiles.append(`AttachFile0${index + 1}`, att.file);
          console.log(`Appending file AttachFile0${index + 1}:`, att.file.name);
        } else {
          console.warn(`Skipping invalid file at index ${index}:`, att);
        }
      });

      for (let [key, value] of formDataWithFiles.entries()) {
        console.log(`FormData Entry: ${key} =`, value instanceof File ? value.name : value);
      }

      const response = await fetch(url, {
        method: "POST",
        body: formDataWithFiles,
      });

      const result = await response.json();
      if (result.result === "success") {
        alert("บันทึกข้อมูลสำเร็จ");
        navigate("/assetsearch");
      } else {
        setError(result.error || "ไม่สามารถบันทึกข้อมูลได้");
      }
    } catch (err) {
      setError("เกิดข้อผิดพลาดในการบันทึกข้อมูล");
      console.error("Submit Error:", err);
    } finally {
      setLoading(false);
    }
  };

  const isFormValid = useMemo(() => {
    return formData.AssetCode.trim() !== "" && formData.AssetName.trim() !== "";
  }, [formData.AssetCode, formData.AssetName]);

  return (
    <Box p={5}>
      <TextSet
        type="header"
        mb={5}
        label={AssetID ? "ข้อมูลครุภัณฑ์" : "เพิ่มข้อมูลครุภัณฑ์"}
        icon="buildingWheat"
      />
      {loading ? (
        <Spinner size="xl" />
      ) : error ? (
        <Alert status="error">
          <AlertIcon />
          {error}
        </Alert>
      ) : (
        <Box mb={5} p={5} borderWidth={1} borderRadius="md" boxShadow="sm">
          <form onSubmit={(e) => {
            e.preventDefault();
            if (isFormValid) {
              handleSubmit();
            } else {
              setError("กรุณากรอกข้อมูลให้ครบถ้วน: รหัสครุภัณฑ์ และ ชื่อครุภัณฑ์");
            }
          }}>
        <Box mt={5} p={5} borderWidth={1} borderRadius="md" boxShadow="sm">
          <TextSet type="subheader" label="ข้อมูลทั่วไป"  icon="buildingColumn" />
            <Grid templateColumns="repeat(4, 1fr)" gap={6}>
              <GridItem colSpan={1}>
                <FormControl isRequired>
                  <FormLabel>รหัสครุภัณฑ์</FormLabel>
                  <Input
                    name="AssetCode"
                    value={formData.AssetCode}
                    onChange={handleChange}
                    readOnly={!!AssetID}
                  />
                </FormControl>
              </GridItem>
              <GridItem colSpan={1}>
                <FormControl>
                  <FormLabel>ปีงบประมาณ</FormLabel>
                  <Input
                    type="number"
                    name="BudgetYear"
                    value={formData.BudgetYear}
                    onChange={handleChange}
                  />
                </FormControl>
              </GridItem>
              <GridItem colSpan={1}>
                <FormControl>
                  <FormLabel>วันที่ได้รับ</FormLabel>
                  <Input
                    type="date"
                    name="AcquisitionDate"
                    value={formData.AcquisitionDate}
                    onChange={handleChange}
                  />
                </FormControl>
              </GridItem>
              <GridItem colSpan={1}>
                <FormControl>
                  <FormLabel>สถานะ</FormLabel>
                  <Select
                    name="Status"
                    value={formData.Status}
                    onChange={handleChange}
                  >
                    <option value="1">ใช้งาน</option>
                    <option value="0">ไม่ใช้งาน</option>
                  </Select>
                </FormControl>
              </GridItem>
              <GridItem colSpan={4}>
                <FormControl isRequired>
                  <FormLabel>ชื่อครุภัณฑ์</FormLabel>
                  <Input
                    name="AssetName"
                    value={formData.AssetName}
                    onChange={handleChange}
                  />
                </FormControl>
              </GridItem>
              <GridItem colSpan={1}>
                <FormControl>
                  <FormLabel>ประเภทครุภัณฑ์</FormLabel>
                  <Select
                    name="AssetCategoryID"
                    value={formData.AssetCategoryID}
                    onChange={handleChange}
                  >
                    <option value="">เลือกประเภทครุภัณฑ์</option>
                    {assetCategories.map((category) => (
                      <option key={category.AssetCategoryID} value={category.AssetCategoryID}>
                        {category.CategoryName}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              </GridItem>
              <GridItem colSpan={1}>
                <FormControl>
                  <FormLabel>ประเภททรัพย์สิน</FormLabel>
                  <Select
                    name="AssetTypeID"
                    value={formData.AssetTypeID}
                    onChange={handleChange}
                  >
                    <option value="">เลือกประเภททรัพย์สิน</option>
                    {assetTypes.map((type) => (
                      <option key={type.AssetTypeID} value={type.AssetTypeID}>
                        {type.TypeName}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              </GridItem>
              <GridItem colSpan={1}>
                <FormControl>
                  <FormLabel>ประเภทการได้มา</FormLabel>
                  <Select
                    name="AcquisitionType"
                    value={formData.AcquisitionType}
                    onChange={handleChange}
                  >
                    <option value="">เลือกประเภทการได้มา</option>
                    {acquisitionTypes.map((acquisition) => (
                      <option key={acquisition.AcquisitionTypeID} value={acquisition.AcquisitionTypeID}>
                        {acquisition.TypeName}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              </GridItem>
              <GridItem colSpan={1}>
                <FormControl>
                  <FormLabel>หน่วยงาน</FormLabel>
                  <Select
                    name="DepartmentID"
                    value={formData.DepartmentID}
                    onChange={handleChange}
                  >
                    <option value="">เลือกหน่วยงาน</option>
                    {departments.map((department) => (
                      <option key={department.DepartmentID} value={department.DepartmentID}>
                        {department.DepartmentName}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              </GridItem>
              <GridItem colSpan={1}>
                <FormControl>
                  <FormLabel>หน่วยต้นทุน</FormLabel>
                  <Select
                    name="CostUnitID"
                    value={formData.CostUnitID}
                    onChange={handleChange}
                  >
                    <option value="">เลือกหน่วยต้นทุน</option>
                    {costCenters.map((costCenter) => (
                      <option key={costCenter.CostCenterID} value={costCenter.CostCenterID}>
                        {costCenter.CostCenterName}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              </GridItem>
              <GridItem colSpan={1}>
                <FormControl>
                  <FormLabel>ราคาต่อหน่วย</FormLabel>
                  <Input
                    type="number"
                    name="UnitPrice"
                    value={formData.UnitPrice}
                    onChange={handleChange}
                  />
                </FormControl>
              </GridItem>
              <GridItem colSpan={1}>
                <FormControl>
                  <FormLabel>จำนวน</FormLabel>
                  <Input
                    type="number"
                    name="Quantity"
                    value={formData.Quantity}
                    onChange={handleChange}
                  />
                </FormControl>
              </GridItem>
              <GridItem colSpan={4}>
                <FormControl>
                  <FormLabel>ข้อมูลเกี่ยวกับครุภัณฑ์</FormLabel>
                  <Textarea
                    name="Remark"
                    value={formData.Remark}
                    onChange={handleChange}
                  />
                </FormControl>
              </GridItem>
              </Grid>
            </Box>
            
        <Box mt={5} p={5} borderWidth={1} borderRadius="md" boxShadow="sm">
          <TextSet type="subheader" label="รูปภาพ และไฟล์ประกอบ"  icon="paperClip" />
            <Grid templateColumns="repeat(4, 1fr)" gap={6}>
              <GridItem colSpan={4}>
                <FormControl>
                  <FormLabel>ไฟล์แนบ (สูงสุด 4 ไฟล์, รูปภาพ, PDF, DOC, DOCX)</FormLabel>
                  <Box {...getRootProps()} borderWidth={2} p={5} borderStyle="dashed" borderRadius="md">
                    <input {...getInputProps()} />
                    <p>ลากและวางไฟล์ที่นี่ หรือคลิกเพื่อเลือกไฟล์ (สูงสุด 4 ไฟล์, ขนาดไม่เกิน 10MB ต่อไฟล์)</p>
                  </Box>
                  {attachments.length > 0 && (
                    <Table variant="simple" mt={4}>
                      <Thead>
                        <Tr>
                          <Th>ชื่อไฟล์</Th>
                          <Th>ตัวอย่าง</Th>
                          <Th>ดูไฟล์</Th>
                          <Th>ลบไฟล์</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {attachments.map((att, index) => (
                          <Tr key={index}>
                            <Td>
                              {att.FileName}
                            </Td>
                            <Td>
                              {att.preview && (att.FileName?.toLowerCase().endsWith(".png") ||
                                att.FileName?.toLowerCase().endsWith(".jpg") ||
                                att.FileName?.toLowerCase().endsWith(".jpeg") ||
                                att.FileName?.toLowerCase().endsWith(".gif")) ? (
                                <img src={att.preview} alt={att.FileName} style={{ maxWidth: "100px", maxHeight: "100px" }} />
                              ) : att.FileName?.toLowerCase().endsWith(".pdf") ? (
                                <FaFilePdf size="50px" color="#FF0000" />
                              ) : (
                                <p>เอกสาร</p>
                              )}
                            </Td>
                            <Td>
                              <Button
                                size="sm"
                                colorScheme="blue"
                                leftIcon={<ViewIcon />}
                                onClick={() => handleViewFile(att)}
                              >
                                ดูไฟล์
                              </Button>
                            </Td>
                            <Td>
                              <Button
                                size="sm"
                                colorScheme="red"
                                leftIcon={<DeleteIcon />}
                                onClick={() => handleDeleteAttachment(index)}
                              >
                                ลบ
                              </Button>
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  )}
                </FormControl>
              </GridItem>
            </Grid>
          </Box>
        <Box mt={5} p={5} borderWidth={1} borderRadius="md" boxShadow="sm">
          <TextSet type="subheader" label="ผู้ขาย และการรับประกัน"  icon="screwdriver" />
            <Grid templateColumns="repeat(4, 1fr)" gap={6}>
              <GridItem colSpan={1}>
                <FormControl>
                  <FormLabel>ชื่อผู้ขาย</FormLabel>
                  <Input
                    name="SupplierBy"
                    value={formData.SupplierBy}
                    onChange={handleChange}
                  />
                </FormControl>
              </GridItem>
              <GridItem colSpan={1}>
                <FormControl>
                  <FormLabel>เบอร์โทรศัพท์</FormLabel>
                  <Input
                    name="SupplierTel"
                    value={formData.SupplierTel}
                    onChange={handleChange}
                  />
                </FormControl>
              </GridItem>
              <GridItem colSpan={1}>
                <FormControl>
                  <FormLabel>อีเมล</FormLabel>
                  <Input
                    name="SupplierEmail"
                    value={formData.SupplierEmail}
                    onChange={handleChange}
                  />
                </FormControl>
              </GridItem>
              <GridItem colSpan={4}>
                <FormControl>
                  <FormLabel>ที่อยู่ผู้ขาย</FormLabel>
                  <Input
                    name="SupplierAddress"
                    value={formData.SupplierAddress}
                    onChange={handleChange}
                  />
                </FormControl>
              </GridItem>
              <GridItem colSpan={1}>
                <FormControl>
                  <FormLabel>ชื่อผู้รับประกัน</FormLabel>
                  <Input
                    name="WarrantyBy"
                    value={formData.WarrantyBy}
                    onChange={handleChange}
                  />
                </FormControl>
              </GridItem>
              <GridItem colSpan={1}>
                <FormControl>
                  <FormLabel>เบอร์โทรศัพท์</FormLabel>
                  <Input
                    name="WarrantyTel"
                    value={formData.WarrantyTel}
                    onChange={handleChange}
                  />
                </FormControl>
              </GridItem>
              <GridItem colSpan={1}>
                <FormControl>
                  <FormLabel>อีเมล</FormLabel>
                  <Input
                    name="WarrantyEmail"
                    value={formData.WarrantyEmail}
                    onChange={handleChange}
                  />
                </FormControl>
              </GridItem>
              <GridItem colSpan={1}>
                <FormControl>
                  <FormLabel>วันที่หมดระยะเวลารับประกัน</FormLabel>
                  <Input
                    type="date"
                    name="WarrantyExpired"
                    value={formData.WarrantyExpired}
                    onChange={handleChange}
                  />
                </FormControl>
              </GridItem>
              <GridItem colSpan={1}>
                <FormControl>
                  <FormLabel>ชื่อผู้ให้บริการบำรุงรักษา</FormLabel>
                  <Input
                    name="MaintenanceBy"
                    value={formData.MaintenanceBy}
                    onChange={handleChange}
                  />
                </FormControl>
              </GridItem>
              <GridItem colSpan={1}>
                <FormControl>
                  <FormLabel>เบอร์โทรศัพท์</FormLabel>
                  <Input
                    name="MaintenanceTel"
                    value={formData.MaintenanceTel}
                    onChange={handleChange}
                  />
                </FormControl>
              </GridItem>
              <GridItem colSpan={1}>
                <FormControl>
                  <FormLabel>อีเมล</FormLabel>
                  <Input
                    name="MaintenanceEmail"
                    value={formData.MaintenanceEmail}
                    onChange={handleChange}
                  />
                </FormControl>
              </GridItem>
              <GridItem colSpan={1}>
                <FormControl>
                  <FormLabel>วันที่หมดระยะเวลาบำรุงรักษา</FormLabel>
                  <Input
                    type="date"
                    name="MaintenanceExpired"
                    value={formData.MaintenanceExpired}
                    onChange={handleChange}
                  />
                </FormControl>
              </GridItem>
              {AssetID && (
                <>
                  <GridItem colSpan={1}>
                    <FormControl>
                      <FormLabel>ผู้สร้างข้อมูล</FormLabel>
                      <Input
                        value={formData.CreateBy}
                        readOnly
                      />
                    </FormControl>
                  </GridItem>
                  <GridItem colSpan={1}>
                    <FormControl>
                      <FormLabel>วันที่สร้างข้อมูล</FormLabel>
                      <Input
                        type="date"
                        value={formData.CreateDate}
                        readOnly
                      />
                    </FormControl>
                  </GridItem>
                  <GridItem colSpan={1}>
                    <FormControl>
                      <FormLabel>ผู้ปรับปรุงข้อมูล</FormLabel>
                      <Input
                        value={formData.UpdatedBy}
                        readOnly
                      />
                    </FormControl>
                  </GridItem>
                  <GridItem colSpan={1}>
                    <FormControl>
                      <FormLabel>วันที่ปรับปรุงข้อมูล</FormLabel>
                      <Input
                        type="date"
                        value={formData.UpdatedDate}
                        readOnly
                      />
                    </FormControl>
                  </GridItem>
                </>
              )}
            </Grid>
            </Box>
            <Box mt={5} display="flex" justifyContent="space-between">
              <IconButton.Back onClick={() => navigate("/assetsearch")} />
              <IconButton.Save type="submit" disabled={!isFormValid} />
            </Box>
          </form>
        </Box>
      )}
    </Box>
  );
};

export default AssetForm;